import { InsightCategoryBadge } from '../../components/badges/InsightCategoryBadge';

import type { ConfidenceLevel, InsightCardVM } from './tela10-adapter';
import type React from 'react';

// Convertido de cards-3.jsx:1402-1443 (window.X → imports; fixtures → VM). Diferenças honestas vs
// o mockup: NÃO renderiza "Sugerido: X" (campo `suggestedAction` é ficção de protótipo, inexistente
// no model Insight) nem o `#id` cru (ULID = ruído). O footer de ações depende do status (D3/D5).

const CONF_COLOR: Record<ConfidenceLevel, string> = {
  'very-high': 'var(--danger-fg)',
  high: 'var(--success-fg)',
  medium: 'var(--warning-fg)',
  low: 'var(--fg-3)',
};

// Nível de confiança em PT (eixo 5 — enum cru nunca chega ao operador).
const CONF_LABEL: Record<string, string> = {
  'very-high': 'muito alta',
  high: 'alta',
  medium: 'média',
  low: 'baixa',
};

// Agente detector humanizado (gestor-insight-agent — Item 41).
const AGENT_LABEL: Record<string, string> = {
  'gestor-insight-agent': 'Agente de insights',
};

export interface InsightCardProps {
  vm: InsightCardVM;
  /** Nome do operador-alvo (targetUserId via usePresence — D1), quando o insight é focal. */
  targetName?: string | null;
  /** Se há `ai-insight-action` (D5) — habilita "Tomar ação" nos insights aceitos. */
  canAction?: boolean;
  onAccept?: (vm: InsightCardVM) => void;
  onFalsePositive?: (vm: InsightCardVM) => void;
  onAction?: (vm: InsightCardVM) => void;
  onArchive?: (vm: InsightCardVM) => void;
}

export function InsightCard({
  vm,
  targetName,
  canAction = false,
  onAccept,
  onFalsePositive,
  onAction,
  onArchive,
}: InsightCardProps): React.JSX.Element {
  // Pendente: aceitar / falso positivo / arquivar. Aceito: tomar ação (gated) / arquivar.
  const isPending = vm.status === 'pending';
  const isAccepted = vm.status === 'accepted';
  const hasFooter =
    (isPending && (onAccept !== undefined || onFalsePositive !== undefined)) ||
    (isAccepted && canAction && onAction !== undefined) ||
    onArchive !== undefined;

  return (
    <article className={`insight-card insight-card--${vm.category}`}>
      <header className="insight-card__head">
        <InsightCategoryBadge category={vm.category} />
        {targetName !== undefined && targetName !== null && (
          <span className="tweaks-hint">· foco: {targetName}</span>
        )}
      </header>

      <p className="insight-card__text">&ldquo;{vm.content}&rdquo;</p>

      <div className="insight-card__meta tweaks-hint">
        Detectado por:{' '}
        <span className="mono">{AGENT_LABEL[vm.detectorAgentId] ?? vm.detectorAgentId}</span>
        {/* Item 478 — confiança só se exibe sobre base que o tenant considera suficiente. */}
        {!vm.insufficientBasis && (
          <>
            {' '}
            · Confiança:{' '}
            <b className="mono" style={{ color: CONF_COLOR[vm.confidenceLevel] }}>
              {vm.confidence.toFixed(2)} ({CONF_LABEL[vm.confidenceLevel]})
            </b>
          </>
        )}{' '}
        · Período: {vm.detectionPeriod} · Baseado em: {vm.basedOnCount} conversa
        {vm.basedOnCount === 1 ? '' : 's'}
      </div>

      {vm.insufficientBasis && (
        <div
          className="modal-form__callout modal-form__callout--warn"
          style={{ fontSize: 'var(--text-xs)' }}
        >
          ⚠ Base insuficiente: este insight foi gerado sobre menos conversas do que o mínimo
          configurado para o tenant. A confiança não é exibida.
        </div>
      )}

      {!vm.insufficientBasis && vm.confidenceLevel === 'low' && (
        <div
          className="modal-form__callout modal-form__callout--warn"
          style={{ fontSize: 'var(--text-xs)' }}
        >
          ⚠ Insight com confiança baixa — considere com cautela
        </div>
      )}

      {vm.drillDown.length > 0 && (
        <details className="insight-card__drilldown">
          <summary>Ver dados subjacentes</summary>
          <dl className="tweaks-hint" style={{ padding: 'var(--space-2)', margin: 0 }}>
            {vm.drillDown.map((pair) => (
              <div key={pair.key} style={{ display: 'flex', gap: 6 }}>
                <dt style={{ fontWeight: 'var(--fw-medium)' }}>{pair.key}:</dt>
                <dd style={{ margin: 0 }}>{pair.value}</dd>
              </div>
            ))}
          </dl>
        </details>
      )}

      {hasFooter && (
        <footer className="insight-card__foot">
          {isPending && onAccept !== undefined && (
            <button
              type="button"
              className="btn-ghost-mini btn-ghost-mini--primary"
              onClick={() => onAccept(vm)}
            >
              Aceitar como pertinente
            </button>
          )}
          {isPending && onFalsePositive !== undefined && (
            <button type="button" className="btn-ghost-mini" onClick={() => onFalsePositive(vm)}>
              Falso positivo
            </button>
          )}
          {isAccepted && canAction && onAction !== undefined && (
            <button
              type="button"
              className="btn-ghost-mini btn-ghost-mini--primary"
              onClick={() => onAction(vm)}
            >
              Tomar ação ▾
            </button>
          )}
          {onArchive !== undefined && (
            <button type="button" className="btn-ghost-mini" onClick={() => onArchive(vm)}>
              Arquivar
            </button>
          )}
        </footer>
      )}
    </article>
  );
}
